import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  orgCapabilitiesQueryOptions,
  projectCapabilitiesQueryOptions,
} from "@/app/capabilities/api/get-capabilities";
import {
  type EligibilityResponse,
  eligibilityQueryOptions,
} from "@/app/onboarding/api/get-eligibility";
import { shouldRedirectToOnboarding } from "@/app/onboarding/lib/selectors";
import {
  AppShellSkeleton,
  setShellHint,
} from "@/components/custom/app-shell-skeleton";
import { OrgProjectRouteError } from "@/lib/org-project-context/constants";
import { OrgProjectErrorFallback } from "@/lib/org-project-context/error-fallbacks";
import { resolveDashboardOrgProjectRoute } from "@/lib/org-project-context/hooks";

export const Route = createFileRoute("/_authenticated/")({
  beforeLoad: async ({ context }) => {
    const result = await resolveDashboardOrgProjectRoute(context.queryClient);

    if (result.outcome === "redirect") {
      // Both destinations are project routes that await these before rendering;
      // on a cold cache, starting them now overlaps them with the eligibility fetch.
      const projectCapabilities = projectCapabilitiesQueryOptions({
        organizationId: result.organizationId,
        projectId: result.projectId,
      });
      if (!context.queryClient.getQueryData(projectCapabilities.queryKey)) {
        context.queryClient.prefetchQuery(projectCapabilities);
      }
      const orgCapabilities = orgCapabilitiesQueryOptions(
        result.organizationId
      );
      if (!context.queryClient.getQueryData(orgCapabilities.queryKey)) {
        context.queryClient.prefetchQuery(orgCapabilities);
      }

      let eligibility: EligibilityResponse;
      try {
        eligibility = await context.queryClient.ensureQueryData(
          eligibilityQueryOptions(result.organizationId, result.projectId)
        );
      } catch {
        throw redirect({
          to: "/orgs/$organizationId/projects/$projectId/playground/chat",
          params: {
            organizationId: result.organizationId,
            projectId: result.projectId,
          },
        });
      }

      const destination = shouldRedirectToOnboarding(eligibility);
      if (destination === "onboarding") {
        setShellHint(false);
      }

      throw redirect({
        to:
          destination === "onboarding"
            ? "/orgs/$organizationId/projects/$projectId/onboarding"
            : "/orgs/$organizationId/projects/$projectId/playground/chat",
        params: {
          organizationId: result.organizationId,
          projectId: result.projectId,
        },
      });
    }

    throw new OrgProjectRouteError(result);
  },
  pendingComponent: AppShellSkeleton,
  errorComponent: OrgProjectErrorFallback,
});
